import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  Edit,
  FileText,
  Users,
  ArrowLeft,
} from "lucide-react";
import { PlanListItem } from "@features/onboarding/utility-setup/types";
import PlanVersionTable from "./PlanVersionList";
import { usePlanDetail } from "@features/onboarding/utility-setup/hooks";

const PlanDetailsView: React.FC = () => {

  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"details" | "history">("details");
  const [expandedServices, setExpandedServices] = useState<{
    [key: string]: boolean;
  }>({});
  
  const planData: PlanListItem = location.state?.initialData;
  console.log("planData from location", planData);
  const {data: planDetail} = usePlanDetail(planData?.id)
  console.log("planDetail from usePlanDetail", planDetail);

  if (!planData) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">No plan data available</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Go Back
        </button>
      </div>
    );
  }
  const formatLabel = (key: string) => {
    return key
      .replace(/[_-]/g, " ") // snake_case or kebab-case to space
      .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase to space
      .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize each word
  };
  const toggleService = (serviceName: string) => {
    setExpandedServices((prev) => ({
      ...prev,
      [serviceName]: !prev[serviceName],
    }));
  };

  const handleEdit = () => {
    navigate(`/plan-tarrif/plan/edit/${planData.id}`, {
      state: { initialData: planData },
    });
  };

  const formatCurrency = (amount: string | number | null) => {
    if (!amount) return "N/A";
    return `$${parseFloat(amount.toString()).toFixed(2)}`;
  };
  const handleGenerateReport = () => {
    // Add your report generation logic here
    console.log("Generate report for plan:", planData.id);
  };

  const handleViewConsumers = () => {
    // Add your view consumers logic here
    console.log("View consumers for plan:", planData.id);
  };
  return (
    <div className="min-h-screen">
      <div className="">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm mb-4">
          <div className="flex items-center justify-between pb-4 border-b">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">
                  {planData.planName}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      planData.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {planData.statusDisplay}
                  </span>
                  <span className="text-sm text-gray-500">
                    Valid: {planData.validity || 'Ongoing'}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={handleEdit}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit Plan
            </button>
          </div>

          {/* Simple Tab Buttons */}
          <div className="flex border-b">
            <button
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === "details"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("details")}
            >
              Plan Details
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === "history"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("history")}
            >
              Version History
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "details" ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <div className="bg-white rounded-md border shadow-sm">
                <div className="p-4">
                  <h2 className="card-heading-text flex items-center gap-2">
                    📋 Basic Information
                  </h2>
                </div>
                <div className="p-4 pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="card-subheading-text">
                        Plan Name
                      </label>
                      <p className="mt-1 text-gray-900">{planData.planName}</p>
                    </div>
                    <div>
                      <label className="card-subheading-text">
                        Short Name
                      </label>
                      <p className="mt-1 text-gray-900">{planData.shortName}</p>
                    </div>
                    <div >
                      <label className="card-subheading-text">
                        Description
                      </label>
                      <p className="mt-1 text-gray-900">
                        {planData.description || "N/A"}
                      </p>
                    </div>

                      <div >
                      <label className="card-subheading-text">
                        Processing Fees
                      </label>
                      <p className="mt-1 text-gray-900">
                        {planData.processingFees || "N/A"}
                      </p>
                    </div>

                      <div >
                      <label className="card-subheading-text">
                        Connection Fee
                      </label>
                      <p className="mt-1 text-gray-900">
                        {planData.connectionFee || "N/A"}
                      </p>
                    </div>

                      <div>
                      <label className="card-subheading-text">
                        Security Deposit
                      </label>
                      <p className="mt-1 text-gray-900">
                        {planData.securityDeposit || "N/A"}
                      </p>
                    </div>

                    <div>
                      <label className="card-subheading-text">
                        Billing Frequency
                      </label>
                      <p className="mt-1 text-gray-900">
                        {planData.billingFrequencyDisplay}
                      </p>
                    </div>
                    <div>
                      <label className="card-subheading-text">
                        Tax Information
                      </label>
                      <p className="mt-1 text-gray-900">
                        {planData.taxTypeDisplay}
                      </p>
                    </div>
                    <div>
                      <label className="card-subheading-text">
                        Consumer Categories
                      </label>
                      <div className="mt-1 flex flex-wrap gap-2">
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">
                          {planData.category}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {planData.subcategory}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Utility Services Details */}
              <div className="bg-white rounded-md border ">
                <div className="p-4">
                  <h2 className="card-heading-text">
                    Utility Services Details
                  </h2>
                  <p className="card-subheading-text">View your attached utility services.</p>
                </div>
                <div className="p-4 pt-0">
                  {planData?.utilityService?.map((service, index) => (
                    <div key={index} className="border rounded-lg mb-4">
                      <button
                        onClick={() => toggleService(service)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-medium text-gray-900">
                          {service}
                        </span>
                        {expandedServices[service] ? (
                          <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                      {expandedServices[service] && (
                        <div className="border-t bg-gray-50">
                          <div className="p-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">
                              Rate Information
                            </h4>
                            {planData.utilityServiceData?.rate?.[service] && (
                              <div className="bg-white rounded-lg p-4 mb-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-xs text-gray-500">
                                      Rate Type
                                    </label>
                                    <p className="text-sm font-medium">
                                      {planData.utilityServiceData.rate[service]
                                        .type === 0
                                        ? "Fixed Rate"
                                        : "Variable Rate"}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="text-xs text-gray-500">
                                      Rate Name
                                    </p>
                                    <p className="text-sm font-medium">
                                    {planDetail?.result?.utilityServiceData?.rate[service]?.rate?.rateName}

                                    </p>
                                  </div>

                                  {/* <div>
                                    <label className="text-xs text-gray-500">
                                      Rate
                                    </label>
                                    <p className="text-sm font-medium">
                                      {
                                        planData.utilityServiceData.rate[
                                          service
                                        ].rate
                                      }
                                    </p>
                                  </div> */}

                                  {planData.utilityServiceData.rate[service]
                                    .version && (
                                    <div>
                                      <label className="text-xs text-gray-500">
                                        Version
                                      </label>
                                      <p className="text-sm font-medium">
                                        {
                                          planData.utilityServiceData.rate[
                                            service
                                          ].version
                                        }
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Common Charges */}
              <div className="bg-white rounded-md border shadow-sm">
                <div className="p-4 border-b">
                  <h2 className="card-heading-text">
                    Common Charges
                  </h2>
                </div>
                <div className="p-4 pt-0">
                  {Object.entries(
                    planData.utilityServiceData?.utilityServiceCharges || {}
                  ).map(([chargeName, chargeData]) => {
                    const isUtilityService =
                      planData.utilityService?.includes(chargeName);
                    const displayValue = isUtilityService
                      ? chargeData.consumption === "full"
                        ? "100%"
                        : `${chargeData.consumptionPercentage || "0"}%`
                      : formatCurrency(chargeData.fee);

                    return (
                      <div
                        key={chargeName}
                        className="flex justify-between items-center py-3 border-b last:border-b-0"
                      >
                        <div>
                          <span className="font-medium text-gray-900">
                            {formatLabel(chargeName)}
                          </span>
                          {isUtilityService && chargeData.consumption && (
                            <p className="text-xs text-gray-500">
                              Consumption: {chargeData.consumption}
                            </p>
                          )}
                        </div>
                        <span className="font-semibold text-gray-900">
                          {displayValue}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Plan Summary */}
              <div className="bg-white rounded-md border shadow-sm">
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900">Plan Summary</h3>
                </div>
                <div className="p-4 pt-0 space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Status:</span>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        planData.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {planData.statusDisplay}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Billing:</span>
                    <span className="text-sm font-medium">
                      {planData.billingFrequencyDisplay}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Tax:</span>
                    <span className="text-sm font-medium">
                      {planData.taxTypeDisplay}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Services:</span>
                    <span className="text-sm font-medium">
                      {planData?.utilityService?.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Consumers:</span>
                    <span className="text-sm font-medium">{planDetail?.result?.consumerCount}</span>
                  </div>

                   <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Created By:</span>
                    <span className="text-sm font-medium">
                      {planData.createdUserRemoteName}
                    </span>
                  </div>

                   <div className="flex justify-between">
                    <span className="text-sm text-gray-500">last updated date:</span>
                    <span className="text-sm font-medium">
                      {planDetail?.result?.lastModifiedDate}
                    </span>
                  </div>

                   <div className="flex justify-between">
                    <span className="text-sm text-gray-500"> last updated By:</span>
                    <span className="text-sm font-medium">
                      {planData.lastModifiedUserRemoteName}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Created:</span>
                    <span className="text-sm font-medium">
                      {planData.createdDate}
                    </span>
                  </div>
                </div>
              </div>

              {/* Plan Consumer Impact */}
              <div className="bg-white rounded-md border shadow-sm">
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    👥 Plan Consumer Impact
                  </h3>
                </div>
                <div className="p-4 pt-0 space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">
                      Total Consumers:
                    </span>
                    <span className="text-sm font-medium">{planDetail?.result?.consumerCount}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    ✓ All consumers are in the {planData.category.toLowerCase()}{" "}
                    category
                  </div>
                  <div className="text-sm text-gray-600">
                    ✓ Changes would affect{" "}
                    {planData?.utilityService?.join(", ").toLowerCase()} service
                    billing
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-1">
                    ⓘ Changes apply next billing cycle
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white p-4 rounded-md border shadow-sm">
            <h2 className="card-heading-text">Version History</h2>
            <PlanVersionTable planId={planData.id} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanDetailsView;
